import { Grid } from '@mui/material';
import AuthService from 'utils/AuthService';
import { Property, PropertyType, Status } from 'types';
import React, { ChangeEvent, FormEvent, ReactNode } from 'react';
import AuturaTextField from 'components/inputs/AuturaTextField';
import { useTranslation } from 'react-i18next';
import AuturaDropdown from 'components/inputs/AuturaDropdown';
import { ValidatorForm } from 'react-material-ui-form-validator';
import FormFrame from 'components/layout/FormFrame';
import SettingsSection from 'components/layout/SettingsSection';
import AuturaStateField from 'components/inputs/AuturaStateField';
import { ScrollableFormContainer } from 'components/layout/ScrollableContainer';
import { CorrectionFormProps } from 'components/importer/PreviewStep';

const PropertyCorrectionForm = ({
  entity: property,
  setEntity: setProperty,
}: CorrectionFormProps<Property>): ReactNode => {
  const { t } = useTranslation();

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!property) return;

    const { name, value } = event.target;
    const prevProperty = { ...property };
    switch (name) {
      case 'name':
        setProperty({ ...prevProperty, name: value });
        break;
      case 'description':
        setProperty({ ...prevProperty, description: value });
        break;
      case 'externalID':
        setProperty({ ...prevProperty, externalID: value });
        break;
      case 'billingCustomerKey':
        setProperty({
          ...prevProperty,
          data: {
            ...prevProperty.data,
            Metadata: {
              ...prevProperty.data.Metadata,
              billingCustomerKey: value,
            },
          },
        });
        break;
      case 'street':
        setProperty({
          ...prevProperty,
          location: { ...prevProperty.location, street: value },
        });
        break;
      case 'city':
        setProperty({
          ...prevProperty,
          location: { ...prevProperty.location, city: value },
        });
        break;
      case 'stateCode':
        setProperty({
          ...prevProperty,
          location: { ...prevProperty.location, stateCode: value },
        });
        break;
      case 'postalCode':
        setProperty({
          ...prevProperty,
          location: { ...prevProperty.location, postalCode: value },
        });
        break;
      case 'notes':
        setProperty({
          ...prevProperty,
          data: {
            ...prevProperty.data,
            settings: { ...prevProperty.data.settings, notes: value },
          },
        });
        break;
    }
  };

  const handleSetPropertyType = (e: ChangeEvent<HTMLInputElement>) => {
    if (!property) return;
    setProperty({ ...property, type: e.target.value as PropertyType });
  };

  return (
    <FormFrame loading={false}>
      <ScrollableFormContainer maxHeight='calc(100dvh - 454px)'>
        <SettingsSection title={''}>
          <Grid container width={'100%'} spacing={1.5}>
            <Grid
              size={{
                xs: 12,
                lg: 6,
              }}
            >
              <AuturaTextField
                name='name'
                label={t('property.name', { ns: 'entities' })}
                value={property?.name || ''}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                lg: 3,
              }}
            >
              <AuturaTextField
                name='description'
                label={t('property.description', { ns: 'entities' })}
                value={property?.description || ''}
                onChange={handleChange}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6,
                lg: 3,
              }}
            >
              <AuturaDropdown
                label={t('property.type', { ns: 'entities' })}
                required
                name='type'
                options={[
                  PropertyType.Residential,
                  PropertyType.Commercial,
                  PropertyType.Apartments,
                ]}
                value={property?.type || PropertyType.Residential}
                setValue={handleSetPropertyType}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6,
                lg: 3,
              }}
            >
              <AuturaTextField
                name='billingCustomerKey'
                label={t('property.data.Metadata.BillingCustomerKey', {
                  ns: 'entities',
                })}
                value={property?.data.Metadata?.billingCustomerKey || ''}
                onChange={handleChange}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6,
                lg: 3,
              }}
            >
              <AuturaTextField
                name='externalID'
                label={t('property.externalID', { ns: 'entities' })}
                value={property?.externalID || ''}
                onChange={handleChange}
                readonly={!AuthService.IsSuperAdmin()}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6,
                lg: 3,
              }}
            >
              <AuturaDropdown
                name='status'
                label={t('property.status', { ns: 'entities' })}
                value={property?.status || Status.Active}
                setValue={(e: ChangeEvent<HTMLInputElement>) => {
                  if (property) {
                    setProperty({
                      ...property,
                      status: e.target.value as Status,
                    });
                  }
                }}
                options={[Status.Active, Status.Disabled]}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6,
                lg: 3,
              }}
            >
              <AuturaTextField
                label={t('property.data.settings.gateCode', {
                  ns: 'entities',
                })}
                name={t('property.data.settings.gateCode', {
                  ns: 'entities',
                })}
                value={property?.data.settings.gateCode || ''}
                onChange={({ target: { value: v } }) => {
                  if (property) {
                    setProperty({
                      ...property,
                      data: {
                        ...property.data,
                        settings: {
                          ...property.data.settings,
                          gateCode: v,
                        },
                      },
                    });
                  }
                }}
              />
            </Grid>
            <Grid size={12}>
              <AuturaTextField
                name='notes'
                label={t('property.data.settings.notes', { ns: 'entities' })}
                value={property?.data?.settings?.notes || ''}
                onChange={handleChange}
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </SettingsSection>
        <SettingsSection
          title={t('subheadings.location', { ns: 'properties' })}
          divider={false}
        >
          <Grid container width={'100%'} spacing={1.5}>
            <Grid
              size={{
                xs: 12,
                lg: 3,
              }}
            >
              <AuturaTextField
                name='street'
                label={t('property.location.street', { ns: 'entities' })}
                value={property?.location.street || ''}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                lg: 3,
              }}
            >
              <AuturaTextField
                name='city'
                label={t('property.location.city', { ns: 'entities' })}
                value={property?.location.city || ''}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                lg: 3,
              }}
            >
              <AuturaStateField
                stateCode={property?.location.stateCode || ''}
                setStateCode={(stateCode: string) => {
                  if (property) {
                    setProperty({
                      ...property,
                      location: { ...property.location, stateCode },
                    });
                  }
                }}
                label={t('property.location.stateCode', { ns: 'entities' })}
                required
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                lg: 3,
              }}
            >
              <AuturaTextField
                name='postalCode'
                label={t('property.location.postalCode', { ns: 'entities' })}
                value={property?.location.postalCode || ''}
                onChange={handleChange}
                required
              />
            </Grid>
          </Grid>
        </SettingsSection>
      </ScrollableFormContainer>
    </FormFrame>
  );
};

export default PropertyCorrectionForm;
